import { X } from 'lucide-react';
import * as React from 'react';

import { cn } from '~/lib/utils';

interface SheetProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
  side?: 'left' | 'right' | 'top' | 'bottom';
}

const SheetContext = React.createContext<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
} | null>(null);

type ClickableElement = React.ReactElement<{
  onClick?: React.MouseEventHandler<HTMLElement>;
}>;

function useSheet() {
  const ctx = React.useContext(SheetContext);
  if (!ctx)
    throw new Error('Sheet compound components must be used within <Sheet>');
  return ctx;
}

export function Sheet({
  open: controlledOpen,
  onOpenChange,
  children,
  side = 'right'
}: SheetProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false);
  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = onOpenChange ?? setUncontrolledOpen;

  const childList = React.Children.toArray(children);
  const inlineChildren = childList.filter(
    (c) => !(React.isValidElement(c) && c.type === SheetContent)
  );
  const overlayChildren = childList.filter(
    (c) => React.isValidElement(c) && c.type === SheetContent
  );

  React.useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, setOpen]);

  return (
    <SheetContext.Provider value={{ open, onOpenChange: setOpen }}>
      {/* Triggers only — SheetContent must not live in document flow (breaks flex parents like h-14 headers). */}
      <div className="contents">{inlineChildren}</div>
      {open && (
        <>
          <div
            data-state="open"
            className="fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
            onClick={() => setOpen(false)}
          />
          <div
            data-state="open"
            className={cn(
              'fixed z-50 bg-background p-0 shadow-lg transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500',
              side === 'left' &&
                'inset-y-0 left-0 h-full w-[min(20rem,85vw)] border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm',
              side === 'right' &&
                'inset-y-0 right-0 h-full w-3/4 border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm',
              side === 'top' &&
                'inset-x-0 top-0 h-auto border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top',
              side === 'bottom' &&
                'inset-x-0 bottom-0 h-auto border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom'
            )}
          >
            {overlayChildren}
          </div>
        </>
      )}
    </SheetContext.Provider>
  );
}

export function SheetTrigger({
  children,
  asChild
}: {
  children: React.ReactNode;
  asChild?: boolean;
}) {
  const { onOpenChange } = useSheet();
  if (asChild && React.isValidElement(children)) {
    const child = children as ClickableElement;
    return React.cloneElement(child, {
      onClick: (event: React.MouseEvent<HTMLElement>) => {
        child.props.onClick?.(event);
        if (!event.defaultPrevented) onOpenChange(true);
      }
    });
  }
  return (
    <button type="button" onClick={() => onOpenChange(true)}>
      {children}
    </button>
  );
}

export function SheetClose({
  children,
  asChild
}: {
  children: React.ReactNode;
  asChild?: boolean;
}) {
  const { onOpenChange } = useSheet();
  if (asChild && React.isValidElement(children)) {
    const child = children as ClickableElement;
    return React.cloneElement(child, {
      onClick: (event: React.MouseEvent<HTMLElement>) => {
        child.props.onClick?.(event);
        if (!event.defaultPrevented) onOpenChange(false);
      }
    });
  }
  return (
    <button type="button" onClick={() => onOpenChange(false)}>
      {children}
    </button>
  );
}

export function SheetContent({
  children,
  className,
  showCloseButton = true
}: {
  children: React.ReactNode;
  className?: string;
  /** Set false when the panel provides its own header close control (e.g. sidebar drawer). */
  showCloseButton?: boolean;
}) {
  return (
    <div className={cn('relative flex h-full min-h-0 flex-col', className)}>
      {showCloseButton ? (
        <SheetClose asChild>
          <button
            type="button"
            className="absolute right-3 top-3 z-10 rounded-md p-2 text-muted-foreground opacity-80 ring-offset-background transition-colors hover:bg-accent hover:text-foreground hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </button>
        </SheetClose>
      ) : null}
      {children}
    </div>
  );
}

export function SheetHeader({
  children,
  className
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-col space-y-2 text-center sm:text-left',
        className
      )}
    >
      {children}
    </div>
  );
}

export function SheetTitle({
  children,
  className
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <h2 className={cn('text-lg font-semibold text-foreground', className)}>
      {children}
    </h2>
  );
}
